/**
 * Production Supabase Orders & Shipping Address Sync Layer.
 * Direct CRUD against Supabase `public.orders`, `public.order_items`, and `public.user_addresses`.
 */

import { supabase } from "@/lib/supabase";
import { decreaseProductStock } from "@/features/admin/products/store/admin-products-store";
import { apiUrl, readJsonResponse } from "@/lib/api-endpoints";

export type SavedAddress = {
  fullName: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
};

export type OrderItem = {
  id: string;
  name: string;
  price: number;
  qty: number;
  image?: string;
};

export type UserOrder = {
  id: string;
  userId: string;
  userEmail?: string;
  userPhone?: string;
  date: string;
  items: OrderItem[];
  itemsSummary: string;
  subtotal: number;
  shippingCost: number;
  gst: number;
  total: number;
  status: "Processing" | "Shipped" | "Delivered" | "Cancelled";
  shippingAddress: SavedAddress;
  paymentMethod: string;
  courierName?: string;
  awbNumber?: string;
};

const ORDERS_KEY = "verdant_user_orders";
const ADDRESS_KEY = "verdant_saved_addresses";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function getAllStoredOrders(): UserOrder[] {
  if (!isBrowser()) return [];
  try {
    const raw = localStorage.getItem(ORDERS_KEY);
    return raw ? (JSON.parse(raw) as UserOrder[]) : [];
  } catch {
    return [];
  }
}

export function getUserOrders(
  userId?: string,
  userEmail?: string,
  userPhone?: string,
): UserOrder[] {
  const all = getAllStoredOrders();
  if (!userId && !userEmail && !userPhone) return [];

  return all.filter((o) => {
    if (userId && o.userId === userId) return true;
    if (userEmail && o.userEmail?.toLowerCase() === userEmail.toLowerCase()) return true;
    if (userPhone && o.userPhone === userPhone) return true;
    return false;
  });
}

/**
 * Look up a specific order by ID.
 * First checks localStorage. If not found locally (or if forceFresh is true),
 * queries /api/get-order and caches the fresh result to localStorage.
 */
export async function fetchOrderById(orderId: string, forceFresh = false): Promise<UserOrder | null> {
  if (!orderId) return null;
  if (!forceFresh) {
    const local = getAllStoredOrders().find((o) => o.id === orderId);
    if (local) return local;
  }

  try {
    const res = await fetch(`${apiUrl("get-order")}?orderId=${encodeURIComponent(orderId)}`);
    if (!res.ok) return null;
    const json = await readJsonResponse<{ success: boolean; order?: UserOrder }>(res, "get-order");
    if (json.success && json.order) {
      await saveUserOrder(json.order);
      return json.order;
    }
  } catch (err) {
    console.warn("fetchOrderById error:", err);
  }
  return null;
}

/** Shape of a `public.orders` row as read from Supabase. */
interface OrderDbRow {
  id: string;
  user_id?: string | null;
  user_email?: string | null;
  customer_email?: string | null;
  user_phone?: string | null;
  phone?: string | null;
  customer_name?: string | null;
  created_at?: string | null;
  items_summary?: string | null;
  items?: OrderDbItem[] | null;
  subtotal?: number | null;
  shipping_cost?: number | null;
  gst?: number | null;
  total?: number | null;
  total_amount?: number | null;
  status?: string | null;
  shipping_address?: SavedAddress | null;
  payment_method?: string | null;
  courier_name?: string | null;
  awb_number?: string | null;
}

/** Shape of a serialized order line stored in `orders.items`. */
interface OrderDbItem {
  id?: string;
  name?: string;
  price?: number;
  unitPrice?: number;
  qty?: number;
  quantity?: number;
  image?: string;
}

/** Fetch user orders directly from Supabase Cloud DB */
export async function fetchUserOrdersFromSupabase(
  userId: string,
  email?: string,
): Promise<UserOrder[]> {
  try {
    let query = supabase.from("orders").select("*");

    if (userId) {
      query = query.or(`user_id.eq.${userId},user_email.eq.${email || ""}`);
    } else if (email) {
      query = query.eq("user_email", email);
    } else {
      return [];
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error || !data) {
      // Fall back to local orders for this user
      return getUserOrders(userId, email);
    }

    const dbOrders = data.map((row: OrderDbRow) => ({
      id: row.id,
      userId: row.user_id || "",
      userEmail: row.user_email || row.customer_email,
      userPhone: row.user_phone || row.phone,
      date: row.created_at
        ? new Date(row.created_at).toLocaleDateString("en-IN", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : new Date().toLocaleDateString("en-IN"),
      itemsSummary:
        row.items_summary ||
        (Array.isArray(row.items)
          ? row.items.map((i: OrderDbItem) => `${i.name} (x${i.qty})`).join(", ")
          : ""),
      subtotal: Number(row.subtotal) || Number(row.total) || 0,
      shippingCost: Number(row.shipping_cost) || 0,
      gst: Number(row.gst) || 0,
      total: Number(row.total) || Number(row.total_amount) || 0,
      status:
        row.status === "DELIVERED"
          ? "Delivered"
          : row.status === "SHIPPED"
            ? "Shipped"
            : row.status === "CANCELLED"
              ? "Cancelled"
              : "Processing",
      courierName: row.courier_name || undefined,
      awbNumber: row.awb_number || undefined,
      shippingAddress: row.shipping_address || {
        fullName: row.customer_name || "Customer",
        address: "Delivery Address",
        city: "City",
        state: "State",
        pincode: "000000",
        phone: row.phone || "",
      },
      paymentMethod: row.payment_method || "RAZORPAY",
      items: Array.isArray(row.items)
        ? row.items.map((item: OrderDbItem) => ({
            id: item.id,
            name: item.name,
            price: Number(item.price || item.unitPrice) || 0,
            qty: Number(item.qty || item.quantity) || 1,
            image: item.image,
          }))
        : [],
    })) as UserOrder[];

    // Merge: prepend local-only orders (e.g. placed as guest or before DB sync)
    const localOrders = getUserOrders(userId, email);
    const dbIds = new Set(dbOrders.map((o) => o.id));
    const localOnly = localOrders.filter((o) => !dbIds.has(o.id));
    return [...localOnly, ...dbOrders];
  } catch (err) {
    console.error("Failed to fetch Supabase orders:", err);
    return getUserOrders(userId, email);
  }
}

/** Save user purchase order directly into Supabase DB */
export async function saveUserOrder(order: UserOrder): Promise<boolean> {
  if (!isBrowser()) return false;

  // 1. Local caching for customer order display
  try {
    const all = getAllStoredOrders();
    const existingIndex = all.findIndex((o) => o.id === order.id);
    if (existingIndex >= 0) {
      all[existingIndex] = { ...all[existingIndex], ...order };
    } else {
      all.unshift(order);
    }
    localStorage.setItem(ORDERS_KEY, JSON.stringify(all));
    window.dispatchEvent(new Event("orders_updated"));
  } catch (err) {
    console.error("Failed to save order locally", err);
  }

  // 2. Local caching for admin dashboard console
  try {
    const rawAdmin = localStorage.getItem("verdant_admin_orders_v1");
    const adminOrders = rawAdmin ? JSON.parse(rawAdmin) : [];
    const newAdminOrder = {
      id: order.id,
      customerName: order.shippingAddress?.fullName || order.userEmail || "Customer",
      email: order.userEmail || "customer@example.com",
      phone: order.shippingAddress?.phone || order.userPhone || "",
      shippingAddress: order.shippingAddress || {
        fullName: "Customer",
        address: "Delivery Address",
        city: "City",
        state: "State",
        pincode: "000000",
        phone: "",
      },
      items: (order.items || []).map((i) => ({
        id: i.id,
        name: i.name,
        sku: `VRD-${i.id}`,
        image: i.image || "/plantpicture/p1/5.webp",
        qty: i.qty || 1,
        unitPrice: i.price,
        totalPrice: i.price * (i.qty || 1),
      })),
      subtotal: Math.round(order.subtotal),
      discount: 0,
      shippingFee: Math.round(order.shippingCost),
      gstAmount: Math.round(order.gst),
      total: Math.round(order.total),
      paymentMethod: order.paymentMethod?.includes("UPI") ? "UPI" : "CREDIT_CARD",
      paymentStatus: "PAID" as const,
      transactionId: `TXN-${order.id.slice(-6)}`,
      status: "PROCESSING" as const,
      date: new Date().toLocaleDateString("en-IN", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      timeline: [
        {
          timestamp: new Date().toISOString(),
          status: "Order Placed",
          description: "Payment confirmed via Razorpay checkout.",
        },
      ],
      notes: [],
    };
    adminOrders.unshift(newAdminOrder);
    localStorage.setItem("verdant_admin_orders_v1", JSON.stringify(adminOrders));
    window.dispatchEvent(new Event("orders_updated"));
    try {
      const bc = new BroadcastChannel("orders_sync");
      bc.postMessage({ type: "ORDERS_UPDATED" });
      bc.close();
    } catch {
      // Ignored
    }
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("recent_order_id", order.id);
    }
  } catch (err) {
    console.error("Failed to sync to admin orders store", err);
  }

  return true;
}

export function getSavedAddress(userId?: string, userEmail?: string): SavedAddress | null {
  if (!isBrowser() || (!userId && !userEmail)) return null;
  try {
    const raw = localStorage.getItem(ADDRESS_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, SavedAddress>;
    return map[userId || ""] || map[userEmail || ""] || null;
  } catch {
    return null;
  }
}

export function saveAddress(key: string, address: SavedAddress): void {
  if (!isBrowser() || !key) return;

  // 1. Local caching
  try {
    const raw = localStorage.getItem(ADDRESS_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, SavedAddress>) : {};
    map[key] = address;
    localStorage.setItem(ADDRESS_KEY, JSON.stringify(map));
  } catch (err) {
    console.error("Failed to save address locally", err);
  }

  // 2. Direct Supabase Cloud DB persistence (only for registered auth UUIDs)
  try {
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
    if (isUuid) {
      supabase
        .from("user_addresses")
        .upsert(
          {
            user_id: key,
            full_name: address.fullName,
            address: address.address,
            city: address.city,
            state: address.state,
            pincode: address.pincode,
            phone: address.phone,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" },
        )
        .then(({ error }) => {
          if (error) {
            console.warn("Supabase address sync notice:", error.message);
          }
        });
    }
  } catch (err) {
    console.warn("Supabase address persist error:", err);
  }
}

export function getStoredAddress(key: string): SavedAddress | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(ADDRESS_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, SavedAddress>) : {};
    return map[key] || null;
  } catch {
    return null;
  }
}
